import React, { useState } from 'react';
import { Box, Typography, TextField, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { get } from 'lodash';
import UrlField from './UrlField';
import { iconSlotsForKeys } from '../../../config/benefitsMenuCatalog';
import { WriteButton, WriteIconButton } from '../../ui';

interface Props {
  label: string;
  description?: string;
  value: unknown;
  onChange: (v: Record<string, string>) => void;
  allData?: unknown;
}

// Derives one upload slot per icon the selected menu items need
// (`X` + `${X}_selected`), reading the menu selections from the full attribute
// object. Picking a menu item above makes its two icon slots appear here,
// ready to upload. Icons not tied to a current menu item are preserved under
// "Other icons" so nothing uploaded is ever lost.
const MenuIconMapField: React.FC<Props> = ({ label, description, value, onChange, allData }) => {
  const map: Record<string, string> = (value && typeof value === 'object' && !Array.isArray(value))
    ? (value as Record<string, string>)
    : {};
  const [newKey, setNewKey] = useState('');

  const menuKeys: string[] = (['primaryMenu', 'hamburgerMenu', 'secondaryMenu'] as const)
    .flatMap((m): string[] => {
      const arr = get(allData, `benefitsOptions.${m}`);
      return Array.isArray(arr) ? (arr as unknown[]).filter((x): x is string => typeof x === 'string') : [];
    });
  const derivedSlots = iconSlotsForKeys(menuKeys);
  const derivedKeys = new Set(derivedSlots.map((s) => s.key));
  const otherKeys = Object.keys(map).filter((k) => !derivedKeys.has(k));

  const setUrl = (k: string, url: string) => onChange({ ...map, [k]: url });
  const deleteKey = (k: string) => { const next = { ...map }; delete next[k]; onChange(next); };
  const addKey = () => {
    const k = newKey.trim();
    if (!k || map[k] !== undefined) return;
    onChange({ ...map, [k]: '' });
    setNewKey('');
  };

  const slotRow = (key: string, rowLabel: string) => (
    <Box key={key} sx={{ p: 1, border: '1px solid #EFF0F0', borderRadius: '8px' }}>
      <UrlField label={rowLabel} value={map[key] || ''} onChange={(v: string) => setUrl(key, v)}
        placeholder="Upload or paste an icon URL" />
    </Box>
  );

  return (
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#141417', mb: 0.5, fontFamily: 'Manrope, sans-serif' }}>{label}</Typography>
      {description && <Typography sx={{ fontSize: 11, color: '#6F6F6F', mb: 1 }}>{description}</Typography>}

      {derivedSlots.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: '#A1A1A1', fontStyle: 'italic' }}>
          Add items to the menus above and their icon slots will appear here.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {derivedSlots.map((s) => slotRow(s.key, s.label))}
        </Box>
      )}

      {otherKeys.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#6F6F6F', mb: 0.75 }}>Other icons</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {otherKeys.map((k) => (
              <Box key={k} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1, border: '1px solid #EFF0F0', borderRadius: '8px' }}>
                <Box sx={{ width: 180, flexShrink: 0, pt: 0.5 }}>
                  <Typography sx={{ fontSize: 12, fontFamily: 'monospace', color: '#141417' }}>{k}</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <UrlField label="" value={map[k] || ''} onChange={(v: string) => setUrl(k, v)} placeholder="Upload or paste an icon URL" />
                </Box>
                <Tooltip title={`Remove "${k}"`}>
                  <WriteIconButton size="small" onClick={() => deleteKey(k)} sx={{ color: '#DC2626', mt: 0.5, '&:hover': { backgroundColor: '#FEE2E2' } }}>
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </WriteIconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center' }}>
        <TextField size="small" placeholder="Extra icon key (e.g. settings)" value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKey(); } }}
          sx={{ width: 240 }} InputProps={{ sx: { fontSize: 12, fontFamily: 'monospace', borderRadius: '6px' } }} />
        <WriteButton size="small" variant="outlined" onClick={addKey}
          disabled={!newKey.trim() || map[newKey.trim()] !== undefined}
          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          sx={{ textTransform: 'none', fontSize: 12, borderRadius: '6px' }}>
          Add icon
        </WriteButton>
      </Box>
    </Box>
  );
};

export default MenuIconMapField;
